import { Typography, Paper, Grid } from '@mui/material';
import { ThemePalette } from 'mui.theme';
import { paperContainerStyle } from 'views/FleetOverview/components/styles';
import { Base } from 'components/Containers/BaseContainer';
import { useAppSelector } from 'store/hook';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface DataMetrics {
  sumIdleFuelConsumption: number;
  sumIdleTimeHours: number;
  assetCount?: number;
}

export const CardSection = ({
  selectedData,
  selectedMakeModel,
  selectedDataMetrics,
  calculateDaysDifference,
  enableDarkTheme = false,
  isProductivity,
}: {
  selectedData: string | null;
  selectedMakeModel: string;
  selectedDataMetrics: DataMetrics | null;
  calculateDaysDifference: any;
  enableDarkTheme: boolean;
  isProductivity: boolean;
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const unitSystem = useAppSelector((state) => state.persistedReducer).customer
    .unitSystem;

  return (
    <Grid item lg={2} md={12} sm={12} xs={12}>
      <Base
        sx={{
          ...paperContainerStyle,
          backgroundColor: 'transparent',
        }}
      >
        <Paper
          elevation={7}
          style={{
            padding: '7px',
            marginBottom: '7px',
            backgroundColor: enableDarkTheme
              ? palette.modalBackground
              : palette.boxBackground,
            color: palette.textPrimary,
            alignContent: 'center',
          }}
        >
          <div>
            <Typography variant="small">{t('asset_s_selected')}</Typography>
            {selectedData ? (
              <Typography variant="h6">{selectedData}</Typography>
            ) : (
              <Typography
                variant="h6"
                sx={{
                  color: '#4A7BFA',
                }}
              >
                {formatMakeModel(selectedMakeModel)} (
                {selectedDataMetrics?.assetCount})
              </Typography>
            )}
          </div>
        </Paper>
        <Paper
          elevation={7}
          style={{
            padding: '7px',
            marginBottom: '7px',
            backgroundColor: enableDarkTheme
              ? palette.modalBackground
              : palette.boxBackground,
            color: palette.textPrimary,
            alignContent: 'center',
          }}
        >
          <div>
            <Typography variant="small">
              {t('fuel_consumed_during')}{' '}
              {isProductivity ? 'production' : 'Idling'}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#4A7BFA',
              }}
            >
              <>
                <span>
                  {selectedDataMetrics?.sumIdleFuelConsumption?.toFixed(2)}
                </span>
                <span
                  style={{
                    color: palette.textPrimary,
                  }}
                >
                  {unitSystem === 'metric' ? ' Litres' : ' Gallons'}
                </span>
              </>
            </Typography>
          </div>
        </Paper>
        <Paper
          elevation={7}
          style={{
            padding: '7px',
            marginBottom: '7px',
            backgroundColor: enableDarkTheme
              ? palette.modalBackground
              : palette.boxBackground,
            color: palette.textPrimary,
          }}
        >
          <div>
            <Typography variant="small">
              {isProductivity ? 'Productive' : 'Idling'} {t('hours')}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#4A7BFA',
              }}
            >
              <>
                <span>{selectedDataMetrics?.sumIdleTimeHours.toFixed(2)}</span>
                <span
                  style={{
                    color: palette.textPrimary,
                  }}
                >
                  {' '}
                  Hrs
                </span>
              </>
            </Typography>
            {/* Add styling or additional components for meters if needed */}
          </div>
        </Paper>
        {!isProductivity ? (
          <Paper
            elevation={7}
            style={{
              padding: '8px',
              marginBottom: '7px',
              backgroundColor: enableDarkTheme
                ? palette.modalBackground
                : palette.boxBackground,
              color: palette.textPrimary,
            }}
          >
            <div>
              <Typography variant="small">
                {t('potential_fuel_savings_for_next_6_months')}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: '#4A7BFA',
                }}
              >
                {selectedDataMetrics && calculateDaysDifference() ? (
                  <>
                    <span>
                      {(
                        (Number(selectedDataMetrics.sumIdleFuelConsumption) /
                          calculateDaysDifference()) *
                        180
                      ).toFixed(2)}
                    </span>
                    <span
                      style={{
                        color: palette.textPrimary,
                      }}
                    >
                      {unitSystem === 'metric' ? ' Litres' : ' Gallons'}
                    </span>
                  </>
                ) : (
                  'N/A'
                )}
              </Typography>
              {/* Add styling or additional components for meters if needed */}
            </div>
          </Paper>
        ) : (
          <></>
        )}
      </Base>
    </Grid>
  );
};

function formatMakeModel(makeModel: string) {
  // Split the makeModel string based on the exclamation mark (!)
  const parts = makeModel.split('!');

  // Capitalize each word in each part
  const formattedParts = parts.map((part) => capitalizeEachWord(part.trim()));

  // Join the parts back together with a space and return the formatted string
  return formattedParts.join(' ');
}

function capitalizeEachWord(str: string) {
  return str.toLowerCase().replace(/(^|\s)\S/g, function (match) {
    return match.toUpperCase();
  });
}
